import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import simbApi from "../../utils/requests";
import logger from "../../lib/logger";

interface UseCheckUserOnboardingOptions {
  /** Skip automatic navigation on unauthorized (useful for AuthorizePage) */
  skipNavigation?: boolean;
  /** Custom callback when user is authorized */
  onAuthorized?: () => void;
  /** Custom callback when user is not authorized */
  onUnauthorized?: () => void;
  /** Custom callback on error */
  onError?: () => void;
}

interface UseCheckUserOnboardingReturn {
  /** Whether the user is authorized */
  isAuthorized: boolean;
  /** Whether the initial check is loading */
  isLoading: boolean;
  /** Whether an error occurred */
  error: boolean;
  /** Whether polling is active */
  isPolling: boolean;
  /** Start polling for authorization status */
  startPolling: () => void;
  /** Stop polling for authorization status */
  stopPolling: () => void;
}

/**
 * Hook to check if a user has completed onboarding/authorization.
 *
 * Handles two main scenarios:
 * 1. Initial check when userId changes - checks auth status and navigates accordingly
 * 2. Polling mode - continuously checks auth status (useful when waiting for OAuth flow)
 *
 * @param userId - The user's ID to check
 * @param options - Configuration options
 * @returns Object containing auth state and control functions
 */
export const useCheckUserOnboarding = (
  userId: number | string | null | undefined,
  options: UseCheckUserOnboardingOptions = {},
): UseCheckUserOnboardingReturn => {
  const { skipNavigation = false, onAuthorized, onUnauthorized, onError } = options;

  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isPolling, setIsPolling] = useState(false);

  const navigate = useNavigate();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const checkOnboarding = useCallback(async () => {
    if (!userId) return null;

    try {
      const response = await simbApi.post("/simb/check-userid-onboarding", {
        userid: userId,
      });

      if (!response.data.error) {
        return response.data.data;
      } else {
        throw new Error("API returned error");
      }
    } catch (err: any) {
      logger.log("error => ", err.message);
      throw err;
    }
  }, [userId]);

  // Initial check when userId changes
  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    const performInitialCheck = async () => {
      setIsLoading(true);
      setError(false);

      try {
        const isOnboarded = await checkOnboarding();

        if (isOnboarded === true) {
          setIsAuthorized(true);
          onAuthorized?.();
        } else if (isOnboarded === false) {
          setIsAuthorized(false);
          if (!skipNavigation) {
            navigate("/onboarding");
          }
          onUnauthorized?.();
        }
      } catch {
        setError(true);
        onError?.();
      } finally {
        setIsLoading(false);
      }
    };

    performInitialCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Polling logic
  useEffect(() => {
    if (!isPolling || !userId) {
      return;
    }

    const pollAction = async () => {
      try {
        const isOnboarded = await checkOnboarding();

        if (isOnboarded) {
          setIsAuthorized(true);
          setIsPolling(false);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          onAuthorized?.();
        }
      } catch (err: any) {
        logger.log("Polling error => ", err.message);
      }
    };

    intervalRef.current = setInterval(pollAction, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPolling, userId, checkOnboarding, onAuthorized]);

  const startPolling = useCallback(() => {
    setIsPolling(true);
  }, []);

  const stopPolling = useCallback(() => {
    setIsPolling(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  return {
    isAuthorized,
    isLoading,
    error,
    isPolling,
    startPolling,
    stopPolling,
  };
};

export default useCheckUserOnboarding;
